/**
 * Admin — Customer Health. Per-user activity since this admin last checked, with problems flagged so they can reach out
 * early. It only reports: "Run it" re-reads the numbers, "Mark as checked" moves the last check. Nothing is sent to anyone.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, CheckCheck, Play } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import { formatCredits, timeAgo } from './customerHealthFormat';

type WindowKey = 'since-check' | '1d' | '7d' | '30d';
type FlagKey = 'failed_or_stuck' | 'charged_no_video' | 'low_credits' | 'email_trouble' | 'gone_quiet' | 'stalled_signup';

interface Flag { key: FlagKey; detail: string }
interface CreditsLeft { milli: number; wallet: 'org' | 'personal'; orgName: string | null }

interface HealthRow {
  userId: string;
  email: string;
  orgs: Array<{ id: string; name: string }>;
  videosSinceCheck: { api: number; web: number };
  videos7d: number;
  lastVideoAt: string | null;
  lastLoginAt: string | null;
  creditsUsedMilli: number;
  creditsLeft: CreditsLeft | null;
  flags: Flag[];
}

interface HealthResponse {
  now: string;
  lastCheckedAt: string | null;
  since: string;
  dormantUsers: number;
  rows: HealthRow[];
}

interface UserVideo { id: string; title: string; createdAt: string; source: 'api' | 'connector' | 'local-mcp' | 'web'; state: string; detail: string; creditsMilli: number }
interface UserEmail { id: string; type: string; status: string; subject: string | null; createdAt: string; bouncedAt: string | null; complainedAt: string | null; reason: string | null }
interface UserDetail { videos: UserVideo[]; emails: UserEmail[] }

const WINDOWS: Array<{ key: WindowKey; label: string }> = [
  { key: 'since-check', label: 'Since last check' },
  { key: '1d', label: '24 hours' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
];

const FLAG_LABEL: Record<FlagKey, string> = {
  failed_or_stuck: 'Failed or stuck',
  charged_no_video: 'Charged, no video',
  low_credits: 'Low credits',
  email_trouble: 'Email trouble',
  gone_quiet: 'Gone quiet',
  stalled_signup: 'Stalled signup',
};

const FLAG_TONE: Record<FlagKey, string> = {
  failed_or_stuck: 'border-destructive/50 text-destructive',
  charged_no_video: 'border-destructive/50 text-destructive',
  low_credits: 'border-amber-500/50 text-amber-600 dark:text-amber-400',
  email_trouble: 'border-amber-500/50 text-amber-600 dark:text-amber-400',
  gone_quiet: 'text-muted-foreground',
  stalled_signup: 'text-muted-foreground',
};

const STATE_TONE: Record<string, string> = {
  failed: 'border-destructive/50 text-destructive',
  stuck: 'border-destructive/50 text-destructive',
  waiting: 'border-amber-500/50 text-amber-600 dark:text-amber-400',
};

const SOURCE_LABEL: Record<UserVideo['source'], string> = { api: 'API', connector: 'Connector', 'local-mcp': 'Local MCP', web: 'Web' };

function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;
}

function Flags({ flags }: { flags: Flag[] }) {
  if (!flags.length) return <span className="text-xs text-muted-foreground">None</span>;
  return (
    <ul className="space-y-1">
      {flags.map((f) => (
        <li key={f.key} className="text-xs">
          <Badge variant="outline" className={cn('mr-1.5', FLAG_TONE[f.key])}>{FLAG_LABEL[f.key]}</Badge>
          <span className="text-muted-foreground break-words">{f.detail}</span>
        </li>
      ))}
    </ul>
  );
}

function CreditsLeftText({ left }: { left: CreditsLeft | null }) {
  if (!left) return <span className="text-muted-foreground">No balance</span>;
  return (
    <span>
      <span className="tabular-nums">{formatCredits(left.milli)}</span>
      <span className="block text-xs text-muted-foreground">{left.wallet === 'org' ? `${left.orgName} wallet` : 'Personal'}</span>
    </span>
  );
}

function UserLink({ row }: { row: HealthRow }) {
  return (
    <span className="min-w-0">
      <Link to="/admin/users" search={{ q: row.email }} onClick={(e) => e.stopPropagation()} className="font-medium underline-offset-2 hover:underline break-all">
        {row.email}
      </Link>
      {row.orgs.length > 0 && <span className="block text-xs text-muted-foreground">{row.orgs.map((o) => o.name).join(', ')}</span>}
    </span>
  );
}

function UserDetailPanel({ detail, error, now }: { detail: UserDetail | undefined; error: string | undefined; now: Date }) {
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!detail) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="min-w-0">
        <h3 className="mb-2 text-sm font-semibold">Videos (latest 50)</h3>
        {detail.videos.length === 0 ? <p className="text-sm text-muted-foreground">No videos yet.</p> : (
          <ul className="divide-y text-sm">
            {detail.videos.map((v) => (
              <li key={v.id} className="py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={STATE_TONE[v.state]}>{v.state}</Badge>
                  <span className="font-medium break-words">{v.title}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground break-words">
                  {SOURCE_LABEL[v.source]} · {timeAgo(v.createdAt, now)} · {formatCredits(v.creditsMilli)} credits · {v.detail}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="min-w-0">
        <h3 className="mb-2 text-sm font-semibold">Recent emails (latest 50)</h3>
        {detail.emails.length === 0 ? <p className="text-sm text-muted-foreground">No emails yet.</p> : (
          <ul className="divide-y text-sm">
            {detail.emails.map((m) => (
              <li key={m.id} className="py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={m.status === 'SKIPPED' || m.status === 'FAILED' ? STATE_TONE.waiting : undefined}>{m.status.toLowerCase()}</Badge>
                  <span className="font-medium">{m.type}</span>
                  {m.bouncedAt && <Badge variant="outline" className={STATE_TONE.failed}>bounced</Badge>}
                  {m.complainedAt && <Badge variant="outline" className={STATE_TONE.failed}>complained</Badge>}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground break-words">
                  {timeAgo(m.createdAt, now)}
                  {(m.status === 'SKIPPED' || m.status === 'FAILED') && <> · Why: {m.reason ?? 'no reason recorded'}</>}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export function CustomerHealth() {
  const [windowKey, setWindowKey] = useState<WindowKey>('since-check');
  const [data, setData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, UserDetail>>({});
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});

  const run = useCallback(async (key: WindowKey) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/admin/customer-health', { params: { window: key } });
      setData(r.data);
      setDetails({});
      setDetailErrors({});
      setOpen(null);
    } catch (err) {
      setError(errorText(err, 'Could not read Customer Health'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { run(windowKey); }, [run, windowKey]);

  async function markChecked() {
    setMarking(true);
    setError(null);
    try {
      await api.post('/admin/customer-health/mark-checked');
      await run(windowKey);
    } catch (err) {
      setError(errorText(err, 'Could not mark as checked'));
    } finally {
      setMarking(false);
    }
  }

  async function toggle(userId: string) {
    if (open === userId) { setOpen(null); return; }
    setOpen(userId);
    if (details[userId]) return;
    try {
      const r = await api.get(`/admin/customer-health/users/${encodeURIComponent(userId)}`);
      setDetails((d) => ({ ...d, [userId]: r.data }));
    } catch (err) {
      setDetailErrors((d) => ({ ...d, [userId]: errorText(err, 'Could not read this user') }));
    }
  }

  const now = data ? new Date(data.now) : new Date();
  const flagged = data?.rows.filter((r) => r.flags.length > 0).length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Customer Health</h1>
          <p className="text-muted-foreground">Who made what since you last checked, and who may need a note from you.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => run(windowKey)} disabled={loading}>
            <Play className="h-4 w-4 mr-2" /> Run it
          </Button>
          <Button onClick={markChecked} disabled={marking || loading}>
            <CheckCheck className="h-4 w-4 mr-2" /> Mark as checked
          </Button>
        </div>
      </div>

      {error && <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Credits window">
          <span className="text-sm text-muted-foreground">Credits used in</span>
          {WINDOWS.map((w) => (
            <Button key={w.key} size="sm" variant={w.key === windowKey ? 'default' : 'outline'} aria-pressed={w.key === windowKey} onClick={() => setWindowKey(w.key)}>
              {w.label}
            </Button>
          ))}
        </div>
        {data && (
          <p className="text-sm text-muted-foreground">
            {data.lastCheckedAt
              ? <>Last checked <b className="text-foreground">{timeAgo(data.lastCheckedAt, now)}</b>.</>
              : <>No check yet, so “since last check” counts the last 7 days.</>}
            {' '}{flagged} of {data.rows.length} flagged.
            {data.dormantUsers > 0 && <> {data.dormantUsers} accounts with no recent activity are not shown.</>}
          </p>
        )}
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
        </div>
      ) : data && (
        <>
          <div className="space-y-3 md:hidden">
            {data.rows.map((row) => (
              <Card key={row.userId}>
                <CardContent className="space-y-3 p-4">
                  <button type="button" onClick={() => toggle(row.userId)} aria-expanded={open === row.userId} aria-label={`Show ${row.email}`} className="float-right p-1">
                    {open === row.userId ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <UserLink row={row} />
                  <Flags flags={row.flags} />
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div><dt className="text-xs text-muted-foreground">Since last check</dt><dd className="tabular-nums">{row.videosSinceCheck.api} API, {row.videosSinceCheck.web} web</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Last 7 days</dt><dd className="tabular-nums">{row.videos7d}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Last video, login</dt><dd>{timeAgo(row.lastVideoAt, now)}, {timeAgo(row.lastLoginAt, now)}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Credits used</dt><dd className="tabular-nums">{formatCredits(row.creditsUsedMilli)}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Credits left</dt><dd><CreditsLeftText left={row.creditsLeft} /></dd></div>
                  </dl>
                  {open === row.userId && <UserDetailPanel detail={details[row.userId]} error={detailErrors[row.userId]} now={now} />}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="hidden md:block rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Since last check</TableHead>
                  <TableHead className="text-right">7 days</TableHead>
                  <TableHead>Last active</TableHead>
                  <TableHead className="text-right">Credits used</TableHead>
                  <TableHead>Credits left</TableHead>
                  <TableHead>Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row) => (
                  <Fragment key={row.userId}>
                    <TableRow className="cursor-pointer" onClick={() => toggle(row.userId)}>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <button type="button" aria-expanded={open === row.userId} aria-label={`Show ${row.email}`} onClick={(e) => { e.stopPropagation(); toggle(row.userId); }} className="mt-0.5">
                            {open === row.userId ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                          <UserLink row={row} />
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">{row.videosSinceCheck.api} API, {row.videosSinceCheck.web} web</TableCell>
                      <TableCell className="text-right tabular-nums">{row.videos7d}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        <span className="block">Video {timeAgo(row.lastVideoAt, now)}</span>
                        <span className="block text-muted-foreground">Login {timeAgo(row.lastLoginAt, now)}</span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatCredits(row.creditsUsedMilli)}</TableCell>
                      <TableCell><CreditsLeftText left={row.creditsLeft} /></TableCell>
                      <TableCell className="max-w-md"><Flags flags={row.flags} /></TableCell>
                    </TableRow>
                    {open === row.userId && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={7} className="bg-muted/30">
                          <UserDetailPanel detail={details[row.userId]} error={detailErrors[row.userId]} now={now} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>

          {data.rows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No active users to show.</p>}
        </>
      )}
    </div>
  );
}
